import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, CssBaseline, Paper as MuiPaper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { spacing } from '@mui/system';
import DashboardItems from '../components/sidebar/dashboardItems';
import Sidebar from '../components/sidebar/Sidebar';
import Loader from '../components/Loader';
import Navbar from '../components/navbar/Navbar';
import MainContent from '../components/MainContent';
import ZendeskWidgetAuthenticator from '../components/ZendeskWidgetAuthenticator';
import { WhatsNewBanner } from '../components/WhatsNewBanner';
import { TrialTermsOverlay } from '../components/TrialTermsOverlay';
import { useAuthContext } from '../contexts/AuthContext';
import { useTermsStatus } from '../hooks/api/useTerms';
const drawerWidth = 210;

const Root = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up('md')} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
`;

const Paper = styled(MuiPaper)(spacing);

const DashboardContent = styled(Paper)`
  flex: 1;
  background: var(--color-light-greyish-blue);
  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }
`;

interface DashboardType {
  children?: React.ReactNode;
}

const Dashboard: React.FC<DashboardType> = ({ children }) => {
  const router = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { loggedInAccount } = useAuthContext();
  // The superadmin never has to accept the trial terms, even when viewing a
  // trial account.
  const checkTerms = !!loggedInAccount && loggedInAccount !== 'admin';
  const { data: terms, isLoading: isLoadingTerms } =
    useTermsStatus(checkTerms);
  const needsToAcceptTerms = !!terms?.required && !terms.accepted;
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const sidebarItems = DashboardItems();

  // Close mobile menu when navigation occurs
  useEffect(() => {
    setMobileOpen(false);
  }, [router.pathname]);

  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

  // Wait for the terms check so a trial page doesn't flash before the overlay.
  // If the check fails, the page shows: the API doesn't enforce the terms.
  if (checkTerms && isLoadingTerms) {
    return <Loader />;
  }

  return (
    <>
      <Root
        // The page behind the terms is blurred and can't be reached; the page
        // itself isn't rendered, so its queries wait until the terms are
        // accepted.
        inert={needsToAcceptTerms}
        style={
          needsToAcceptTerms
            ? { filter: 'blur(4px)', pointerEvents: 'none' }
            : undefined
        }
      >
        <CssBaseline />
        <Drawer>
          <Box
            component="aside"
            aria-label="Mobile navigation"
            sx={{ display: { xs: 'block', lg: 'none' } }}
          >
            <Sidebar
              PaperProps={{
                style: { width: drawerWidth },
              }}
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              pages={sidebarItems}
            />
          </Box>
          <Box
            component="aside"
            aria-label="Desktop navigation"
            sx={{ display: { xs: 'none', md: 'block' } }}
          >
            <Sidebar
              PaperProps={{ style: { width: drawerWidth } }}
              pages={sidebarItems}
            />
          </Box>
        </Drawer>
        <AppContent>
          <ZendeskWidgetAuthenticator />
          <Navbar onDrawerToggle={handleDrawerToggle} />
          <MainContent>
            <DashboardContent p={isLgUp ? 12 : 5}>
              {!needsToAcceptTerms && (
                <>
                  {children}
                  <WhatsNewBanner />
                  <Outlet />
                </>
              )}
            </DashboardContent>
          </MainContent>
        </AppContent>
      </Root>
      {needsToAcceptTerms && <TrialTermsOverlay />}
    </>
  );
};

export default Dashboard;
